/**
 * Per-app state of the motion columns (W2-04a): one `MotionSession` per AppServices, shared by the column factory,
 * the commands, the headers and the gesture handlers. It holds view-only state that is not part of the document
 * and not part of the contracted editor store:
 *
 *  - the sampled-curve cache (render/timeline/curveCache.ts) of every motion lane;
 *  - the last used curve (new segments after the end of a track copy it, DESIGN.md §5.6.1) and the copied curve;
 *  - segments produced by an approximate shape-preserving split in this session (the "≈ split" badge);
 *  - locked value ranges, ranges frozen during a drag, hidden Σ overlays, lanes showing raw events;
 *  - hover, drag readout and the last pointer position per column (context menu commands act there);
 *  - the current column specs and the timeline host they were laid out on.
 * Per-project parts are cleared when the open project (the DocStore) changes.
 */

import { LINEAR } from '../../core/ease/index';
import type { Ease } from '../../core/ease/types';
import type { KeyId } from '../../core/model/types';
import type { Unsubscribe } from '../../core/util/types';
import type { AppServices } from '../../app/registry/types';
import type { TimelineHostApi, TimelineLayer } from '../../render/types';
import { createCurveCache, type CurveCache } from '../../render/timeline/curveCache';
import { asTimelineHostController } from '../../render/timeline/TimelineHost';
import type { MotionHit } from '../../render/timeline/motion/motionScene';
import type { DocStore } from '../../state/types';
import type { ColumnSpec } from './columnSpec';

export interface ValueRange {
  readonly lo: number;
  readonly hi: number;
}

export interface HoverState {
  readonly instanceId: string;
  readonly hit: MotionHit | null;
  readonly lane: number;
  /** Beat (f64) and column x under the pointer. */
  readonly beat: number;
  readonly x: number;
  readonly y: number;
}

export interface DragFeedback {
  readonly instanceId: string;
  readonly lane: number;
  readonly beat: number;
  readonly value: number;
  readonly text: string;
}

export class MotionSession {
  readonly curves: CurveCache = createCurveCache();
  /** Curve of the last easing the user chose (initially Linear). */
  lastCurve: Ease = LINEAR;
  /** Curve copied with "Copy curve". */
  copiedCurve: Ease | null = null;
  /** Segment start key -> the ease it had right after an approximate split (badge while unchanged). */
  readonly approx = new Map<KeyId, Ease>();
  /** Lane key -> value range locked by the user. */
  readonly lockedRanges = new Map<string, ValueRange>();
  /** Lane key -> the value range last drawn (Lock the value range keeps it). */
  readonly lastRanges = new Map<string, ValueRange>();
  /** Lane key -> value range frozen while a key is dragged (the axis must not rescale under the pointer). */
  readonly frozenRanges = new Map<string, ValueRange>();
  /** `<line>:<prop>` whose Σ overlay is hidden. */
  readonly sigmaHidden = new Set<string>();
  /** Lane keys showing raw events instead of keys. */
  readonly rawShown = new Set<string>();
  /** `<line>:<prop>` whose Guided "uses N layers" question was answered with "Edit the base layer". */
  readonly guidedAck = new Set<string>();
  /** The Guided "uses N layers" question is open (a second edit meanwhile does nothing). */
  guidedAsking = false;
  /**
   * The host's element of a motion column (set by the column controller; null when the column is not laid out).
   * Commands focus it so the keyboard router sees `focus.motion`.
   */
  columnElementOf: (id: string) => HTMLElement | null = () => null;
  hover: HoverState | null = null;
  /** Last position of the pointer over a motion column (also kept after it leaves). */
  lastPointer: HoverState | null = null;
  drag: DragFeedback | null = null;
  specs: readonly ColumnSpec[] = [];
  host: TimelineHostApi | null = null;
  /** Bumped whenever session state that changes a scene changes. */
  version = 0;
  private store: DocStore | null = null;
  private readonly listeners = new Set<() => void>();

  services: AppServices;

  constructor(services: AppServices) {
    this.services = services;
  }

  /** Clears the per-project state when the open project changed. */
  syncStore(): void {
    const store = this.services.store;
    if (store === this.store) return;
    this.store = store;
    this.approx.clear();
    this.lockedRanges.clear();
    this.frozenRanges.clear();
    this.rawShown.clear();
    this.guidedAck.clear();
    this.guidedAsking = false;
    this.hover = null;
    this.lastPointer = null;
    this.drag = null;
    this.curves.clear();
    this.touch();
  }

  /** Marks scenes stale and tells listeners (headers). */
  touch(): void {
    this.version++;
    for (const cb of this.listeners) cb();
  }

  subscribe(cb: () => void): Unsubscribe {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }

  /** The current timeline host (the last one that asked for instances, else the services' one). */
  timeline(): TimelineHostApi | null {
    return this.services.timeline ?? this.host;
  }

  /** The beat under the mouse: the host's last hover over any column, else the last position over a motion column. */
  pointerBeat(): number | null {
    const host = this.timeline();
    const ctl = host === null ? null : asTimelineHostController(host);
    const info = ctl?.hoverInfo() ?? null;
    if (info !== null && Number.isFinite(info.beat)) return info.beat;
    return this.lastPointer?.beat ?? null;
  }

  /** Redraws a layer of every motion column (or of one). */
  invalidate(layer: TimelineLayer = 'content', id?: string): void {
    const host = this.timeline();
    if (host === null) return;
    try {
      if (id !== undefined) host.invalidate(id, layer);
      else for (const s of this.specs) host.invalidate(s.id, layer);
    } catch {
      // The host may be mid-dispose; nothing to redraw then.
    }
  }

  relayout(): void {
    const host = this.timeline();
    if (host === null) return;
    try {
      host.relayout();
    } catch {
      // ignore: the host is being torn down
    }
  }
}

/**
 * Sessions are keyed by the editor store: command contexts are copies or prototype children of the services
 * object, so the services object itself is not a stable key, while every context shares its editor store.
 */
const sessions = new WeakMap<object, MotionSession>();

/**
 * The session of an app. `root` = the services object itself (module `init`, the column factory): the session then
 * reads live members (store, engine, timeline) through it; command contexts only look the session up.
 */
export function motionSession(services: AppServices, root = false): MotionSession {
  let s = sessions.get(services.editor);
  if (s === undefined) {
    s = new MotionSession(services);
    sessions.set(services.editor, s);
  } else if (root && s.services !== services) {
    s.services = services;
  }
  s.syncStore();
  return s;
}
